import { useMemo } from 'react';
import { Navigate, ViewProps } from 'react-big-calendar';
import TimeGrid from 'react-big-calendar/lib/TimeGrid';
import { DateLocalizer } from 'react-big-calendar/lib/localizers/date-fns';

import type { IEvent } from '../../../lib';

interface IWeekProps extends ViewProps {
	date: Date;
	localizer: DateLocalizer;
	max?: Date;
	min?: Date;
	scrollToTime?: Date;
	events: IEvent[];
}

const Week = ({
	date,
	localizer,
	max = localizer.endOf(new Date(), 'day'),
	min = localizer.startOf(new Date(), 'day'),
	scrollToTime = localizer.startOf(new Date(), 'day'),
	events,
	...props
}: IWeekProps) => {
	const currRange = useMemo(() => Week.range(date, { localizer }), [date, localizer]);

	return (
		<TimeGrid
			date={date}
			localizer={localizer}
			max={max}
			min={min}
			range={currRange}
			scrollToTime={scrollToTime}
			events={events}
			{...props}
		/>
	);
};

Week.range = (date: Date, { localizer }: { localizer: DateLocalizer }): Date[] => {
	// Set start date to the beginning of the day
	const start = localizer.startOf(date, 'week');
	const end = localizer.add(start, 6, 'day');

	let current = start;
	const range: Date[] = [];

	while (localizer.lte(current, end, 'day')) {
		range.push(current);
		current = localizer.add(current, 1, 'day');
	}

	return range;
};

Week.navigate = (date: Date, action: Navigate, { localizer }: { localizer: DateLocalizer }): Date => {
	switch (action) {
		case Navigate.PREVIOUS:
			return localizer.add(date, -7, 'day');
		case Navigate.NEXT:
			return localizer.add(date, 7, 'day');
		default:
			return date;
	}
};

Week.title = (date: Date): string => {
	return `Week of ${date.toLocaleDateString()}`;
};

export default Week;
